import { Component, h } from 'preact'
import Header from '../header'
import axios from 'axios'
import { DashboardTimezoneDisclaimer } from './DashboardTimezoneDisclaimer'
import { KeyInsightCard } from './KeyInsightCard'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import {FilterOptionDate, IdeaStatus, MetricItem, StoredState, TotalVoteData} from './interfaces'
import {
  getDefaultDateRange,
  trackDashboardUsage,
  getDefaultSelectedDate,
  createDefaultMetricItem,
  previousPeriodDifference,
  getBrandBlue,
  getBrandBorderGrey,
  getBrandGrey,
  getBrandYellow,
  displayLegendsWithMarginBottom
} from './utils'
import DashboardDateRangeFilter from './DashboardDateRangeFilter'
import QueryParameters from './QueryParameters'
import { subscribe } from '../../utils/globals'
import { SetupIdeaStatusMappingWarning } from './ProductFeedbackDashboard/SetupIdeaStatusMappingWarning'
import ButtonControl from '../button-control'
import Icon from '../assets'
import { IdeaStatusMappingModal } from './ProductFeedbackDashboard/IdeaStatusMappingModal'
import AlertComponent from '../ui-components/AlertComponent'
import { ChartData, ChartOptions, ChartTooltipItem } from 'chart.js'
import { GraphTitle} from './GraphTitle'
import { Loader } from './Loader'
import { Line } from 'react-chartjs-2'
import PercentageTotalVotesPerMonth from './ProductFeedbackDashboard/PercentageTotalVotesPerMonth'
displayLegendsWithMarginBottom()

interface Props {}

interface State {
  selectedPresetDateRangeId: string,
  startDate: Date,
  endDate: Date,
  selectedDate: FilterOptionDate,
  presetDateRangeList: Array<FilterOptionDate>,
  isDateRangeOpen: boolean,
  isLoading: boolean,
  isCheckingStatusMapping: boolean,
  storedState: StoredState,
  isKeyInsightLoading: boolean,
  isSidebarMenuOpen: boolean,
  showModal: boolean,
  shouldChartRedraw: boolean,
  storedStatusList: Array<IdeaStatus>,
  ideaStatusList: Array<IdeaStatus>,
  isIdeaStatusMappingSetup: boolean,
  unmappedIdeaStatusCount?: number,
  showErrorMessage: boolean,
  messageType?: string | null,
  message?: string,
  ideasCreated: MetricItem,
  ideasDelivered: number,
  votesDelivered: number,
  votesAdded: MetricItem,
  percentageTotalDeliveredVotes: number,
  cumulativeTotalVotesOverTime: Array<TotalVoteData>,
  isLoadingCumulativeTotalVotesOverTime: boolean
}

class ProductFeedbackDashboard extends Component<Props, State> {
  wrapperRef: null | HTMLElement = null
  barChart: null | HTMLElement = null
  horizontalBarChart: null | HTMLElement = null
  resizeTimeout: number | undefined
  private brandBlue = getBrandBlue()
  private brandBorderGrey = getBrandBorderGrey()
  private brandGrey = getBrandGrey()
  private brandYellow = getBrandYellow()
  constructor () {
    super()
    let monthStartDate = AnalyticsDateFormatter.getMonthStartDate()
    let yesterday = AnalyticsDateFormatter.getYesterday()
    const queryParams = new QueryParameters()
    const defaultMetricItem = createDefaultMetricItem()
    this.setState({
      selectedPresetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
      storedState: {
        presetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
        selectedDateRange: getDefaultSelectedDate()
      },
      startDate: monthStartDate,
      endDate: yesterday,
      presetDateRangeList: [],
      isDateRangeOpen: false,
      isLoading: false,
      isCheckingStatusMapping: true,
      isKeyInsightLoading: false,
      shouldChartRedraw: false,
      isSidebarMenuOpen: true,
      showModal: false,
      storedStatusList: [],
      ideaStatusList: [],
      isIdeaStatusMappingSetup: false,
      unmappedIdeaStatusCount: undefined,
      messageType: null,
      message: '',
      ideasCreated: defaultMetricItem,
      ideasDelivered: 0,
      votesDelivered: 0,
      votesAdded: defaultMetricItem,
      percentageTotalDeliveredVotes: 0,
      cumulativeTotalVotesOverTime: [],
      isLoadingCumulativeTotalVotesOverTime: false
    })

    this.toggleDateRange = this.toggleDateRange.bind(this)
    this.onDateRangeChanged = this.onDateRangeChanged.bind(this)
    this.onResetDateRange = this.onResetDateRange.bind(this)
    this.onPresetDateRangeChanged = this.onPresetDateRangeChanged.bind(this)
    this.onCustomDateRangeChanged = this.onCustomDateRangeChanged.bind(this)

    this.onIdeaStatusMappingChanged = this.onIdeaStatusMappingChanged.bind(this)
    this.showIdeaStatusMappingModal = this.showIdeaStatusMappingModal.bind(this)
    this.saveIdeaStatusMapping = this.saveIdeaStatusMapping.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  async componentDidMount () {
    trackDashboardUsage('product-feedback')
    subscribe('control_sidebar_state', (payload) => this.setState({ isSidebarMenuOpen: payload.isOpen }))
    this.redrawGraphsOnResize()
    try {
      const presetDateRanges = await this.fetchDateRangeFilter()
      let updatedState = Object.assign({}, this.state.storedState)
      let defaultDateRange = getDefaultDateRange(presetDateRanges, this.state.selectedPresetDateRangeId)
      updatedState.presetDateRangeId = defaultDateRange.id
      updatedState.selectedDateRange = defaultDateRange
      if ('custom_range' === this.state.selectedPresetDateRangeId) {
        const queryParameters = new QueryParameters()
        defaultDateRange = {
          id: 'custom_range',
          label: 'Custom range',
          startDate: new Date(queryParameters.get('date_from')),
          endDate: new Date(queryParameters.get('date_till'))
        }
        updatedState.selectedDateRange = getDefaultSelectedDate()
      }

      const { data }  = await axios.get('/community/api/ideas/ideaStatuses')
      if (!data.result.length) {
        this.setState({
          isCheckingStatusMapping: false,
          isIdeaStatusMappingSetup: false,
          showModal: false
        })
        return
      }

      let isIdeaStatusMappingCount = 0
      data.result.forEach((item: IdeaStatus) => {
        item.backgroundColor = `#${item.backgroundColor}`
        item.textColor = `#${item.textColor}`
        if (item.type === '') {
          isIdeaStatusMappingCount = isIdeaStatusMappingCount + 1;
        }
      })

      this.setState({
        ideaStatusList: data.result,
        storedStatusList: data.result,
        isLoading: false,
        isCheckingStatusMapping: false,
        presetDateRangeList: presetDateRanges,
        selectedDate: defaultDateRange,
        startDate: new Date(defaultDateRange.startDate),
        endDate: new Date(defaultDateRange.endDate),
        storedState: updatedState
      })

      if (isIdeaStatusMappingCount === this.state.ideaStatusList.length) {
        this.setState({
          showModal: true,
          isIdeaStatusMappingSetup: false,
          isCheckingStatusMapping: false,
          unmappedIdeaStatusCount: isIdeaStatusMappingCount
        })
        return
      }
      this.setState({
        showModal: false,
        isIdeaStatusMappingSetup: true,
        isCheckingStatusMapping: false,
        unmappedIdeaStatusCount: isIdeaStatusMappingCount
      })

      this.refetchData()
    } catch {
      this.setState({ isLoading: false })
    }

  }

  addHandler () {
    const { isDateRangeOpen } = this.state
    if (isDateRangeOpen) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.onResetDateRange()
      this.setState({}, this.removeHandler)
    }
  }

  redrawGraphsOnResize ()  {
    if (typeof window.ResizeObserver === "undefined") {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimeout)
      this.setState({ shouldChartRedraw: true })
      this.resizeTimeout = window.setTimeout(() => this.setState({ shouldChartRedraw: false }), 2*10)
    })

    if(this.barChart != null) {
      resizeObserver.observe(this.barChart)
    }
  }

  async fetchDateRangeFilter () {
    const res = await axios.get('/analyticsV2/fetchDateRangeFilter')
    return res.data.ranges
  }

  toggleDateRange (value: boolean) {
    this.setState({ isDateRangeOpen: value }, this.addHandler)
  }

  onPresetDateRangeChanged (selectedDate: FilterOptionDate) {
    this.setState({
      selectedPresetDateRangeId: selectedDate.id,
      selectedDate: selectedDate
    })
  }

  onCustomDateRangeChanged (dates: Date[]) {
    const customSelectedDate = {
      id: 'custom_range',
      label: 'Custom Range',
      startDate: dates[0],
      endDate: dates[1]
    }
    this.setState({
      selectedDate: customSelectedDate,
      selectedPresetDateRangeId: customSelectedDate.id
    })
  }

  onDateRangeChanged () {
    const { selectedDate, selectedPresetDateRangeId } = this.state
    let updatedState = Object.assign({}, this.state.storedState)
    updatedState.presetDateRangeId = selectedPresetDateRangeId
    updatedState.selectedDateRange = selectedDate
    this.setState({
      isDateRangeOpen: false,
      startDate: new Date(selectedDate.startDate),
      endDate: new Date(selectedDate.endDate),
      storedState: updatedState
    })
    const queryString = new QueryParameters()
    queryString.set('date_range', selectedPresetDateRangeId)
    if (selectedPresetDateRangeId != 'custom_range') {
      queryString.unset('date_from')
        .unset('date_till')
    } else {
      queryString.set('date_from', AnalyticsDateFormatter.formatDateForUrl(selectedDate.startDate))
        .set('date_till', AnalyticsDateFormatter.formatDateForUrl(selectedDate.endDate))
    }
    window.history.pushState(null, 'date range changed', queryString.getQueryString())
    this.refetchData()
  }

  onResetDateRange () {
    const { presetDateRangeId, selectedDateRange } = this.state.storedState
    this.setState({
      isDateRangeOpen: false,
      selectedPresetDateRangeId: presetDateRangeId,
      selectedDate: selectedDateRange
    })
  }

  closeModal () {
    this.setState({
      showModal: false,
      showErrorMessage: false,
      ideaStatusList: this.state.storedStatusList
    })
  }

  showIdeaStatusMappingModal () {
    this.setState({
      showModal: true
    })
  }

  async saveIdeaStatusMapping () {
    const { ideaStatusList } = this.state
    let hasUnmappedIdeaStatus = ideaStatusList.some((item: IdeaStatus) => item.type === '')
    if (hasUnmappedIdeaStatus) {
      this.setState({
        showErrorMessage: true
      })
      return
    }
    try {
      await axios.post('/ideas/updateIdeaStatusType', { mappedIdeaStatusList: this.state.ideaStatusList })
      this.setState({
        showModal: false,
        showErrorMessage: false,
        isIdeaStatusMappingSetup: true,
        unmappedIdeaStatusCount: 0,
        storedStatusList: ideaStatusList,
        messageType: 'success',
        message: 'Ideation status mapping updated successfully!'
      })
      this.refetchData()
    } catch {
      this.setState({
        messageType: 'error',
        message: 'Something went wrong, ideation statuses can\'t be updated. Please try again.'
      })
    }
  }

  onIdeaStatusMappingChanged (item: IdeaStatus, status: string) {
    let updatedList = this.state.ideaStatusList.map(item => ({...item}))

    const matchedIndex = updatedList.findIndex((updatedListItem: IdeaStatus) => item.id === updatedListItem.id)
    updatedList[matchedIndex].type = status

    this.setState({
      ideaStatusList: updatedList,
      showErrorMessage: false
    })
  }

  async fetchKeyInsights() {
    this.setState({ isKeyInsightLoading: true})
    try {
      const res = await this.fetchData('/analyticsV2/productFeedbackKeyInsights')
      const { ideasCreated, votesAdded, ideasDelivered, votesDelivered } = res.data
      const ideasCreatedPercentageDifference = previousPeriodDifference(ideasCreated.count, ideasCreated.previousCount)
      const votesAddedPercentageDifference = previousPeriodDifference(votesAdded.count, votesAdded.previousCount)
      this.setState({
        ideasCreated: {
          count: ideasCreated.count,
          previousCount: ideasCreated.previousCount,
          percentageDifference: ideasCreatedPercentageDifference
        },
        votesAdded: {
          count: votesAdded.count,
          previousCount: votesAdded.previousCount,
          percentageDifference: votesAddedPercentageDifference
        },
        ideasDelivered:  ideasDelivered.count,
        votesDelivered:  votesDelivered.count,
        isKeyInsightLoading: false
      })
    } catch {
      this.setState({isKeyInsightLoading: false})
    }
  }

  async fetchTotalDeliveredVotesOverTime () {
    this.setState({ isLoadingCumulativeTotalVotesOverTime: true})
    try {
      const res = await this.fetchData('/analyticsV2/totalVotesOverTime', 'daily')
      if (res.data.length !== 0) {
        const dataForInsight = res.data.slice(-1).pop()
        let percentTotalVotesDelivered = 0
        const totalVotes = dataForInsight.open + dataForInsight.delivered
        if (totalVotes > 0) {
          percentTotalVotesDelivered = (dataForInsight.delivered / totalVotes) * 100
        }
        this.setState({
          percentageTotalDeliveredVotes: Number(percentTotalVotesDelivered.toFixed(2)),
        })
      }

      this.setState({
        cumulativeTotalVotesOverTime: res.data,
        isLoadingCumulativeTotalVotesOverTime: false
      })
    } catch {
      this.setState({isLoadingCumulativeTotalVotesOverTime: false})
    }
  }

  async fetchData (url: string, group: string = '') {
    const { startDate, endDate } = this.state
    let filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false)
    }

    if (group !== '') {
      Object.assign(filterOptions, {group: group})
    }

    return axios.post(url, {
      filters: filterOptions
    })
  }

  refetchData () {
    this.fetchKeyInsights()
    this.fetchTotalDeliveredVotesOverTime()
  }

  getChartOptions (): ChartOptions {
    return {
      title: {
        display: false
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 24
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        intersect: false,
        mode: 'index',
        callbacks: {
          title (item: ChartTooltipItem[], data: ChartData): string | string[] {
            if (item.length > 0 && item[0].label) {
              const date = new Date(item[0].label.substr(0, 10))
              const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date)
              const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date)
              return `${month} ${day}`
            }
            return ''
          }
        }
      },
      legend: {
        display: true,
        position: 'top',
        align: 'start',
        labels: {
          boxWidth: 12
        }
      },
      scales: {
        xAxes: [{
          type: 'time',
          offset: true,
          time: {
            minUnit: 'day'
          },
          gridLines: {
            display: false
          },
          ticks: {
            maxRotation: 0,
            callback: (value, index, values) => {
              return '      ' + value + '      '
            }
          }
        }],
        yAxes: [
          {
            gridLines: {
              borderDash: [8, 4],
              color: this.brandBorderGrey,
              drawBorder: false
            },
            stacked: true,
            ticks: {
              padding: 16,
              beginAtZero: true
            }
          }
        ]
      },
      plugins: {
        crosshair: {
          line: {
            color: this.brandGrey, // crosshair line color
            width: 0.5 // crosshair line width
          },
          zoom: {
            enabled: false
          },
          sync: {
            enabled: false
          }
        }
      }
    }
  }

  getCumulativeVotesOverTime (data: Array<TotalVoteData>) {
    return {
      labels: data.map(d => d.date),
      datasets: [{
          label: 'Delivered votes (cumulative)', // Name the series
          data: data.map(d => d.delivered),
          fill: true,
          lineTension: 0,
          pointRadius: 0,
          borderColor: this.brandBlue, // Add custom color border (Line)
          backgroundColor: this.brandBlue, // Add custom color background (Points and Fill)
          borderWidth: 1 // Specify bar border width
        },
        {
          label: 'Open votes (cumulative)', // Name the series
          data: data.map(d => d.open), // Specify the data values array
          fill: true,
          lineTension: 0,
          pointRadius: 0,
          borderColor: this.brandYellow, // Add custom color border (Line)
          backgroundColor: this.brandYellow, // Add custom color background (Points and Fill)
          borderWidth: 1 // Specify bar border width
        },
        {
          label: 'Closed votes (cumulative)', // Name the series
          data: data.map(d => d.closed), // Specify the data values array
          fill: true,
          lineTension: 0,
          pointRadius: 0,
          borderColor: this.brandGrey, // Add custom color border (Line)
          backgroundColor: this.brandGrey, // Add custom color background (Points and Fill)
          borderWidth: 1 // Specify bar border width
        }]
    }
  }
  render (_: Props, { isKeyInsightLoading, shouldChartRedraw, presetDateRangeList, isDateRangeOpen, selectedPresetDateRangeId, startDate, endDate, isLoading, isCheckingStatusMapping, selectedDate,  isSidebarMenuOpen, showModal, isIdeaStatusMappingSetup, unmappedIdeaStatusCount, ideaStatusList, showErrorMessage, messageType, message, ideasCreated, votesAdded, ideasDelivered, votesDelivered, percentageTotalDeliveredVotes, cumulativeTotalVotesOverTime, isLoadingCumulativeTotalVotesOverTime }: State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false

    return (
      <div className={`product-feedback-dashboard-overview dashboard-overview ${isSidebarMenuOpen ? 'dashboard-overview-open-menu' : 'dashboard-overview-closed-menu'}`}
           style={{ width: `calc(100vw - ${isSidebarMenuOpen ? '376' : '152'}px)` }}>
        {
          messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
        }
        <Header
          title='Product Feedback Dashboard'
          disableCancelButton
          disableSaveButton
          isBeta
          customButtons={[<ButtonControl
              newStyle
              class='edit-status-mapping-button'
              onClick={this.showIdeaStatusMappingModal} size='medium'
              title={<div><Icon customClass='edit-icon' icon='edit' height={16} width={16} />Edit status mapping</div>}
          />]}
        />
        {
          !isCheckingStatusMapping && (!isIdeaStatusMappingSetup || unmappedIdeaStatusCount && unmappedIdeaStatusCount != 0 ? <SetupIdeaStatusMappingWarning count={unmappedIdeaStatusCount} openModal={this.showIdeaStatusMappingModal}/> : null)
        }
       <div className='filter-container' ref={node => this.wrapperRef = node}>
          <DashboardDateRangeFilter isLoading={isLoading}
                                    startDate={startDate}
                                    endDate={endDate}
                                    isDropdownOpen={isDateRangeOpen}
                                    items={presetDateRangeList}
                                    selectedDate={selectedDate}
                                    selectedPresetId={selectedPresetDateRangeId}
                                    toggleDropdown={this.toggleDateRange}
                                    onChange={this.onDateRangeChanged}
                                    onReset={this.onResetDateRange}
                                    onCustomDateChange={this.onCustomDateRangeChanged}
                                    onPresetDateChange={this.onPresetDateRangeChanged} />
        </div>
        <div className='metric-container'>
          <KeyInsightCard title='% Total votes delivered'
                          total={percentageTotalDeliveredVotes}
                          isPercentage
                          titleTooltip='The percentage of total cumulative votes delivered up to the end of the selected time period. Excludes closed votes.'
                          isLoading={isLoadingCumulativeTotalVotesOverTime}
                          hideComparisionWithPreviousPeriod
                          isKeyInsightAvailable={isIdeaStatusMappingSetup}
                          keyInsightUnavailableMessage="There's no data yet" />
          <KeyInsightCard title='Votes delivered'
                          total={votesDelivered}
                          titleTooltip='The number of idea votes delivered in the selected time period.'
                          isLoading={isKeyInsightLoading}
                          hideComparisionWithPreviousPeriod
                          comparisionTooltip='The percentage difference between the number of idea votes delivered in the current/previous time period.'                                       isKeyInsightAvailable={isIdeaStatusMappingSetup}
                          keyInsightUnavailableMessage="There's no data yet" />
          <KeyInsightCard title='Votes added'
                          total={votesAdded.count}
                          titleTooltip='The number of votes added to ideas in the selected time period.'
                          percent={votesAdded.percentageDifference}
                          showComparisionUnavailable={votesAdded.previousCount === 0}
                          isLoading={isKeyInsightLoading}
                          comparisionTooltip='The percentage difference between the number of votes added to ideas in the current/previous time period.'
                          isKeyInsightAvailable={isIdeaStatusMappingSetup}
                          keyInsightUnavailableMessage="There's no data yet" />
          <KeyInsightCard title='Ideas delivered'
                          total={ideasDelivered}
                          titleTooltip='The number of ideas delivered in the selected time period.'
                          isLoading={isKeyInsightLoading}
                          comparisionTooltip='The percentage difference between the number of ideas delivered in the current/previous time period.'
                          hideComparisionWithPreviousPeriod
                          isKeyInsightAvailable={isIdeaStatusMappingSetup}
                          keyInsightUnavailableMessage="There's no data yet" />
          <KeyInsightCard title='Ideas created'
                          total={ideasCreated.count}
                          titleTooltip='The number of ideas created in the selected time period.'
                          isLoading={isKeyInsightLoading}
                          percent={ideasCreated.percentageDifference}
                          comparisionTooltip='The percentage difference between the number of ideas created in the current/previous time period.'
                          showComparisionUnavailable={ideasCreated.previousCount === 0}
                          isKeyInsightAvailable={isIdeaStatusMappingSetup}
                          keyInsightUnavailableMessage="There's no data yet" />
        </div>
        <div className='graph-wrapper'>
          <div className='graph-container' ref={element => { this.barChart = element }}>
            <PercentageTotalVotesPerMonth shouldChartRedraw={shouldChartRedraw} isIdeaStatusMappingSetup={isIdeaStatusMappingSetup} startDate={startDate} endDate={endDate} />
            <div className={`graph-row ${!isIdeaStatusMappingSetup ? 'graph-row-no-data' : ''}`}>
              <GraphTitle customClass='with-legend' title='Total votes'
                          tooltipMessage='The cumulative total of open, delivered, and closed votes over time.' />
              {!isIdeaStatusMappingSetup
                  ? <div className='graph-row-no-data-text'>There's no data yet</div>
                  : isLoadingCumulativeTotalVotesOverTime ? <Loader />
                      : <Line redraw={shouldChartRedraw} data={this.getCumulativeVotesOverTime(cumulativeTotalVotesOverTime)} options={this.getChartOptions()} />
              }
            </div>
          </div>
        </div>
        <IdeaStatusMappingModal isIdeaStatusMappingSetup={isIdeaStatusMappingSetup}
                                isOpen={showModal}
                                showError={showErrorMessage}
                                list={ideaStatusList}
                                onClose={this.closeModal}
                                onSave={this.saveIdeaStatusMapping}
                                onChangeCallback={this.onIdeaStatusMappingChanged}/>
        <DashboardTimezoneDisclaimer />
      </div>)
  }
}


export default ProductFeedbackDashboard
